import { gettext } from '@eventbrite/i18n';
import React, { useRef } from 'react';
import { LISTING_HEAP_IDS } from '../../../constants';
import { useScrollOnCommand } from '../../../contexts';
import {
    STATSIG_EVENT_NAMES,
    useG2KExperiment,
} from '../../../experimentation';
import { RefundPolicyProps } from '../../../types';
import { useTrackComponent } from '../../hooks/useTrackComponent';
import {
    EventDetailsMainInner,
    EventDetailsSection,
    EventDetailsSectionTitle,
} from '../EventDetails';

const RefundPolicyType = {
    FLEXIBLE: 'flexible',
    MODERATE: 'moderate',
    STRICT: 'strict',
    NO_REFUNDS: 'no_refunds',
    CUSTOM: 'custom',
};

const generateRefundPolicyDescription = (
    refundPolicyCode: string,
    validDays: number,
) => {
    if (validDays && validDays > 0) {
        return gettext('Refunds up to %(days)s days before event.', {
            days: validDays,
        });
    }

    if (refundPolicyCode) {
        const policyMessages = {
            [RefundPolicyType.FLEXIBLE]: gettext(
                'Refunds up to 1 day before event.',
            ),
            [RefundPolicyType.MODERATE]: gettext(
                'Refunds up to 7 days before event.',
            ),
            [RefundPolicyType.STRICT]: gettext(
                'Refunds up to 30 days before event.',
            ),
            [RefundPolicyType.NO_REFUNDS]: gettext('No refunds'),
        };

        if (policyMessages[refundPolicyCode]) {
            return policyMessages[refundPolicyCode];
        }
    }

    return gettext(
        'The organizer will review refund requests on a case-by-case basis.',
    );
};

export const RefundPolicy = ({
    isPaidEvent,
    refundPolicyCode,
    validDays,
}: RefundPolicyProps) => {
    const ref = useRef<HTMLDivElement>(null);
    useScrollOnCommand(ref, 'refundPolicy');
    useTrackComponent({
        ref,
        heap: {
            eventName: LISTING_HEAP_IDS.REFUND_POLICY_VIEW,
        },
        statsig: {
            eventName: STATSIG_EVENT_NAMES.REFUND_POLICY_VIEW,
        },
    });

    if (useG2KExperiment()) {
        return null;
    }

    if (!isPaidEvent) {
        return null;
    }

    const description = generateRefundPolicyDescription(
        refundPolicyCode || '',
        validDays || 0,
    );

    return (
        <EventDetailsMainInner>
            <EventDetailsSection ariaLabelledBy="refund-policy-heading">
                <span ref={ref} />
                <EventDetailsSectionTitle>
                    <h2>{gettext('Refund Policy')}</h2>
                </EventDetailsSectionTitle>

                <div>{description}</div>
            </EventDetailsSection>
        </EventDetailsMainInner>
    );
};
